import { useState, useEffect } from "react";
import { fetchAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Trash2, Plus, Save } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileUpload } from "@/components/admin/FileUpload";

export default function MetadataManager() {
  const [categories, setCategories] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>({});
  
  const [newCategory, setNewCategory] = useState("");
  const [newCountry, setNewCountry] = useState({ name: "", code: "" });
  
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    fetchAPI("?type=categories").then((data: any) => setCategories(data));
    fetchAPI("?type=countries").then((data: any) => setCountries(data));
    fetchAPI("?type=settings").then((data: any) => setSettings(data));
  };

  const addCategory = async () => {
    if (!newCategory) return;
    await fetchAPI("?type=categories", {
        method: "POST",
        body: JSON.stringify({ name: newCategory })
    });
    setNewCategory("");
    fetchData();
  };

  const deleteCategory = async (id: number) => {
    await fetchAPI(`?type=categories&id=${id}`, { method: "DELETE" });
    fetchData();
  };

  const addCountry = async () => {
     if (!newCountry.name) return;
     await fetchAPI("?type=countries", {
        method: "POST",
        body: JSON.stringify(newCountry)
    });
    setNewCountry({ name: "", code: "" });
    fetchData();
  };

  const deleteCountry = async (id: number) => {
    await fetchAPI(`?type=countries&id=${id}`, { method: "DELETE" });
    fetchData();
  };

  const saveSettings = async () => {
    await fetchAPI("?type=settings", {
        method: "POST",
        body: JSON.stringify(settings)
    });
    toast({ title: "Settings Saved", description: "Global settings updated successfully." });
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-serif font-bold">Metadata & Settings</h1>
      
      <Tabs defaultValue="settings">
        <TabsList>
            <TabsTrigger value="settings">General Settings</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
            <TabsTrigger value="countries">Countries</TabsTrigger>
        </TabsList>

        <TabsContent value="settings">
            <Card>
                <CardHeader>
                    <CardTitle>Site Configuration</CardTitle>
                    <CardDescription>Manage global website appearance.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">

                    <div className="space-y-2">
                        <Label>Site Logo</Label>
                        <FileUpload 
                            value={settings.site_logo || ""} 
                            onChange={(url) => setSettings({...settings, site_logo: url})} 
                            accept="image/*"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Social Share Image (Open Graph)</Label>
                        <FileUpload 
                            value={settings.og_image || ""} 
                            onChange={(url) => setSettings({...settings, og_image: url})} 
                            accept="image/*"
                            placeholder="Upload image for social sharing (1200x630 rec.)"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Hero Background Type</Label>
                        <Select 
                            value={settings.hero_background_type || "image"} 
                            onValueChange={(val) => setSettings({...settings, hero_background_type: val})}
                        >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="image">Image</SelectItem>
                                <SelectItem value="video">Video</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label>Hero Background File</Label>
                        <FileUpload 
                            value={settings.hero_background_url || ""} 
                            onChange={(url) => setSettings({...settings, hero_background_url: url})}
                            accept={settings.hero_background_type === "video" ? "video/*" : "image/*"}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Above Footer Background (CTA)</Label>
                        <FileUpload 
                            value={settings.cta_background_image || ""} 
                            onChange={(url) => setSettings({...settings, cta_background_image: url})}
                            accept="image/*"
                            placeholder="Upload CTA background image"
                        />
                    </div>
                    
                    <div className="pt-4 border-t">
                        <h3 className="font-medium mb-4">Introduction Section</h3>
                         <div className="space-y-2">
                            <Label>Introduction Main Image</Label>
                            <FileUpload 
                                value={settings.introduction_image || ""} 
                                onChange={(url) => setSettings({...settings, introduction_image: url})}
                                accept="image/*"
                            />
                        </div>
                    </div>

                    <div className="pt-4 border-t">
                        <h3 className="font-medium mb-4">Baelleaves Page Images</h3>
                        <div className="grid gap-4">
                            <div className="space-y-2">
                                <Label>Temple Interior Image (Mission Section)</Label>
                                <FileUpload 
                                    value={settings.baelleaves_temple_image || ""} 
                                    onChange={(url) => setSettings({...settings, baelleaves_temple_image: url})}
                                    accept="image/*"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Spiritual Guide Image</Label>
                                <FileUpload 
                                    value={settings.baelleaves_guide_image || ""} 
                                    onChange={(url) => setSettings({...settings, baelleaves_guide_image: url})}
                                    accept="image/*"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="pt-4 border-t">
                        <h3 className="font-medium mb-4">Contact & Social Media</h3>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Contact Email</Label>
                                <Input value={settings.contact_email || ""} onChange={(e) => setSettings({...settings, contact_email: e.target.value})} placeholder="info@baelleaves.com" />
                            </div>
                            <div className="space-y-2">
                                <Label>WhatsApp Number</Label>
                                <Input value={settings.contact_whatsapp || ""} onChange={(e) => setSettings({...settings, contact_whatsapp: e.target.value})} placeholder="+91..." />
                            </div>
                            <div className="space-y-2">
                                <Label>Facebook URL</Label>
                                <Input value={settings.social_facebook || ""} onChange={(e) => setSettings({...settings, social_facebook: e.target.value})} placeholder="https://facebook.com/..." />
                            </div>
                            <div className="space-y-2">
                                <Label>Instagram URL</Label>
                                <Input value={settings.social_instagram || ""} onChange={(e) => setSettings({...settings, social_instagram: e.target.value})} placeholder="https://instagram.com/..." />
                            </div>
                            <div className="space-y-2">
                                <Label>YouTube URL</Label>
                                <Input value={settings.social_youtube || ""} onChange={(e) => setSettings({...settings, social_youtube: e.target.value})} placeholder="https://youtube.com/..." />
                            </div>
                        </div>
                    </div>
                    <Button onClick={saveSettings} className="gap-2">
                        <Save className="w-4 h-4" /> Save Changes
                    </Button>
                </CardContent>
            </Card>
        </TabsContent>

        <TabsContent value="categories">
            <Card>
                <CardHeader>
                    <CardTitle>Content Categories</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex gap-2">
                        <Input 
                            placeholder="New Category Name" 
                            value={newCategory} 
                            onChange={(e) => setNewCategory(e.target.value)} 
                        />
                        <Button onClick={addCategory}><Plus className="w-4 h-4" /></Button>
                    </div>
                    <div className="border rounded-md">
                        <Table>
                            <TableBody>
                                {categories.map(c => (
                                    <TableRow key={c.id}>
                                        <TableCell>{c.name}</TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="sm" onClick={() => deleteCategory(c.id)}>
                                                <Trash2 className="w-4 h-4 text-destructive" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </TabsContent>

        <TabsContent value="countries">
             <Card>
                <CardHeader>
                    <CardTitle>Supported Countries</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex gap-2">
                        <Input 
                            placeholder="Country Name" 
                            value={newCountry.name} 
                            onChange={(e) => setNewCountry({...newCountry, name: e.target.value})} 
                        />
                        <Input 
                            placeholder="Code (e.g. US)" 
                            className="w-24"
                            value={newCountry.code} 
                            onChange={(e) => setNewCountry({...newCountry, code: e.target.value})} 
                        />
                        <Button onClick={addCountry}><Plus className="w-4 h-4" /></Button>
                    </div>
                    <div className="border rounded-md max-h-[400px] overflow-y-auto">
                        <Table>
                            <TableBody>
                                {countries.map(c => (
                                    <TableRow key={c.id}>
                                        <TableCell>{c.name}</TableCell>
                                        <TableCell className="text-muted-foreground">{c.code}</TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="sm" onClick={() => deleteCountry(c.id)}>
                                                <Trash2 className="w-4 h-4 text-destructive" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
